import React, { useEffect, useRef, useState } from "react";
import Conversation from "@/model/interface/Conversation";
import { Badge } from "@/components/ui/badge";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import { cn } from "@/lib/utils";
import { formatDistanceToNow } from "date-fns";
import { toast } from "sonner";
import { getInitials } from "@/util/string";
import { apiErrorMessage } from "@/util/apiError";
import { ArchiveRestore, Trash2, User } from "lucide-react";
import {
  useArchiveConversationMutation,
  useDeleteConversationPermanentlyMutation,
  useRestoreConversationMutation,
} from "@/store/rest_api";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";

interface ConversationListItemProps {
  conversation: Conversation;
  selected: boolean;
  highlighted?: boolean;
  archived?: boolean;
  onSelect: (conversation: Conversation) => void;
  onArchived?: () => void;
  onRestored?: () => void;
  onDeleted?: () => void;
}

export function getDisplayName(conversation: Conversation): string {
  const participant = conversation.participants[0];
  if (!participant) {
    return "Unknown";
  }
  if (participant.contact) {
    const name = [participant.contact.firstName, participant.contact.lastName].filter(Boolean).join(" ");
    if (name) {
      return name;
    }
  }
  return participant.phoneNumber;
}

export function getConversationInitials(conversation: Conversation): string | null {
  const participant = conversation.participants[0];
  if (!participant) return null;
  if (participant.contact) {
    const initials = getInitials(participant.contact.firstName, participant.contact.lastName);
    if (initials && initials !== "?") return initials;
  }
  return null;
}

const ConversationListItem: React.FC<ConversationListItemProps> = ({
  conversation,
  selected,
  highlighted,
  archived = false,
  onSelect,
  onArchived,
  onRestored,
  onDeleted,
}) => {
  const [archiveConversation] = useArchiveConversationMutation();
  const [restoreConversation] = useRestoreConversationMutation();
  const [deleteConversationPermanently, { isLoading: isPurging }] = useDeleteConversationPermanentlyMutation();
  const [confirmArchive, setConfirmArchive] = useState(false);
  const [confirmPurge, setConfirmPurge] = useState(false);
  const rowRef = useRef<HTMLButtonElement>(null);

  const handleArchive = async () => {
    try {
      await archiveConversation(conversation.id).unwrap();
      setConfirmArchive(false);
      onArchived?.();
    } catch (error) {
      toast.error(apiErrorMessage(error, "Failed to archive conversation"));
    }
  };

  const handleRestore = async (event: React.MouseEvent) => {
    event.stopPropagation();
    try {
      await restoreConversation(conversation.id).unwrap();
      toast.success("Conversation restored");
      onRestored?.();
    } catch (error) {
      toast.error(apiErrorMessage(error, "Failed to restore conversation"));
    }
  };

  const handlePurge = async () => {
    try {
      await deleteConversationPermanently(conversation.id).unwrap();
      toast.success("Conversation deleted permanently");
      setConfirmPurge(false);
      onDeleted?.();
    } catch (error) {
      toast.error(apiErrorMessage(error, "Failed to delete conversation"));
    }
  };

  useEffect(() => {
    if (!highlighted) {
      return;
    }
    rowRef.current?.scrollIntoView({ block: "center", behavior: "smooth" });
  }, [highlighted]);

  return (
    <>
      <button
        ref={rowRef}
        onClick={() => onSelect(conversation)}
        className={cn(
          "group w-full flex items-start gap-3 p-3 text-left hover:bg-accent transition-colors border-b border-border",
          selected && "bg-accent",
          highlighted && "bg-primary/10",
        )}
      >
        <Avatar className="h-10 w-10 shrink-0">
          <AvatarFallback>
            {getConversationInitials(conversation) ?? <User className="h-4 w-4" />}
          </AvatarFallback>
        </Avatar>
        <div className="flex-1 min-w-0">
          <div className="flex items-center justify-between">
            <span className="font-medium text-sm truncate">
              {getDisplayName(conversation)}
              {conversation.isGroup && (
                <span className="text-muted-foreground ml-1">
                  +{conversation.participants.length - 1}
                </span>
              )}
            </span>
            <div className="flex items-center gap-1 shrink-0 ml-2">
              {conversation.unreadCount > 0 && (
                <Badge variant="default" className="h-5 min-w-[20px] flex items-center justify-center text-xs">
                  {conversation.unreadCount}
                </Badge>
              )}
              {archived ? (
                <>
                  <ArchiveRestore
                    className="h-3.5 w-3.5 text-muted-foreground hover:text-foreground opacity-0 group-hover:opacity-100 transition-opacity cursor-pointer"
                    onClick={(event) => void handleRestore(event)}
                    aria-label="Restore conversation"
                  />
                  <Trash2
                    className="h-3.5 w-3.5 text-muted-foreground hover:text-destructive opacity-0 group-hover:opacity-100 transition-opacity cursor-pointer"
                    onClick={(event) => {
                      event.stopPropagation();
                      setConfirmPurge(true);
                    }}
                    aria-label="Delete conversation permanently"
                  />
                </>
              ) : (
                <Trash2
                  className="h-3.5 w-3.5 text-muted-foreground hover:text-destructive opacity-0 group-hover:opacity-100 transition-opacity cursor-pointer"
                  onClick={(event) => {
                    event.stopPropagation();
                    setConfirmArchive(true);
                  }}
                  aria-label="Archive conversation"
                />
              )}
            </div>
          </div>
          {conversation.fromPhoneNumber && (
            <p className="text-xs text-muted-foreground truncate mt-0.5">
              via {conversation.fromPhoneNumber.alias || conversation.fromPhoneNumber.number}
            </p>
          )}
          <div className="flex items-center gap-1 mt-0.5">
            <span className="text-sm text-muted-foreground truncate">
              {conversation.lastMessagePreview || "No messages yet"}
            </span>
            {conversation.lastMessageAt && (
              <span className="text-xs text-muted-foreground shrink-0">
                · {formatDistanceToNow(new Date(conversation.lastMessageAt), { addSuffix: true })}
              </span>
            )}
          </div>
        </div>
      </button>

      <AlertDialog open={confirmArchive} onOpenChange={setConfirmArchive}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Archive conversation</AlertDialogTitle>
            <AlertDialogDescription>
              The conversation leaves your inbox and its texts leave the contact's thread. Nothing is deleted, and it comes back on its own if this number texts again.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={() => void handleArchive()} className="bg-destructive text-destructive-foreground hover:bg-destructive/90">
              Archive
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <AlertDialog open={confirmPurge} onOpenChange={setConfirmPurge}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete conversation permanently</AlertDialogTitle>
            <AlertDialogDescription>
              Every text and attachment in this conversation is deleted for good. Consent and do-not-call records for the number stay. This cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={() => void handlePurge()} disabled={isPurging} className="bg-destructive text-destructive-foreground hover:bg-destructive/90">
              Delete permanently
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
};

export default ConversationListItem;
